import {
	createActionsHook,
	createContainer,
	createDefaultEocWrapper,
	createStore,
	createHook,
} from '@atlassian/eoc-rss-helpers';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { emptyResource } from '../../../common/constants/index.tsx';
import type { StoreBaseContainerProps } from '../../../common/types/index.tsx';
import { actions } from './actions/index.tsx';
import type { IncidentsState } from './types.tsx';

export const initialState: IncidentsState = {
	incidentsCopyStatus: emptyResource,
};

const Store = createStore({ initialState, actions, name: 'incidents' });

const IncidentsContainer = createContainer<
	IncidentsState,
	typeof actions,
	{
		children?: React.ReactNode;
		isGlobal?: boolean;
	}
>(Store, {
	Wrapper: createDefaultEocWrapper({
		useSyncContainerProps: (): StoreBaseContainerProps => {
			const { cloudId } = useTenantContext();
			return {
				cloudId,
			};
		},
	}),
});

const useIncidentsStore = createHook(Store);
const useIncidentsActions = createActionsHook(Store);

const useIncidents = createHook(Store, {
	selector: (state) => {
		const { incidentsCopyStatus } = state;
		const projectKey = incidentsCopyStatus.data?.payload?.projectKey;
		const status = incidentsCopyStatus.data?.status;
		const errorLength = incidentsCopyStatus.data?.error?.length ?? 0;
		const hasCopyOperationError = errorLength > 0 || status === 'ERROR';
		const isCopied = status === 'SUCCESS' && !!projectKey && errorLength === 0;
		const isCopying = status === 'IN_PROGRESS';
		const canMigrateAgain = incidentsCopyStatus.data?.canMigrateAgain;
		const isEligibleForCopy =
			((status === 'SUCCESS' || hasCopyOperationError) && canMigrateAgain) ||
			status === 'NOT_STARTED';
		const shouldDisplaySuccessBanner = isCopied && canMigrateAgain === false;

		return {
			projectKey,
			hasCopyOperationError,
			isCopied,
			isCopying,
			isEligibleForCopy,
			shouldDisplaySuccessBanner,
		};
	},
});

export { IncidentsContainer, useIncidentsStore, useIncidentsActions, useIncidents };
